/**
 * CoachYumixIntroModal
 * ====================
 * Epic first-launch cinematic that introduces "Coach YUMIX" with the
 * wall-break video. Plays ONCE per user (localStorage flag), with an
 * optional manual replay from the Réglages page.
 *
 * Final beat (audio): « N'ayez pas peur, le changement est là. »
 *
 * Behaviour:
 *   • Video starts MUTED (browsers block sound autoplay) — a big "tap to
 *     enable sound" button overlays the first second, then mutes itself.
 *   • A "Passer" link appears after 2 s.
 *   • When the video ends → CTA "Commencer l'aventure".
 *   • Esc / backdrop click are intentionally disabled to keep the moment
 *     marked — only "Passer" or "Commencer" close the modal.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, SkipForward, Sparkles } from 'lucide-react';

export const COACH_YUMIX_INTRO_FLAG = 'yumix_coach_intro_seen_v1';

export const hasSeenCoachYumixIntro = () => {
  try {
    return localStorage.getItem(COACH_YUMIX_INTRO_FLAG) === '1';
  } catch {
    return true; // fail-safe : do not show if storage is broken
  }
};

export const markCoachYumixIntroSeen = () => {
  try {
    localStorage.setItem(COACH_YUMIX_INTRO_FLAG, '1');
  } catch {
    /* noop */
  }
};

export const resetCoachYumixIntro = () => {
  try {
    localStorage.removeItem(COACH_YUMIX_INTRO_FLAG);
  } catch {
    /* noop */
  }
};

const CoachYumixIntroModal = ({ open, onClose, language = 'fr' }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [showSkip, setShowSkip] = useState(false);
  const [ended, setEnded] = useState(false);
  const [started, setStarted] = useState(false);

  // Reveal "Passer" after 2 s
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => setShowSkip(true), 2000);
    return () => clearTimeout(t);
  }, [open]);

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setEnded(false);
      setStarted(false);
      setShowSkip(false);
      setMuted(true);
    }
  }, [open]);

  // Attempt autoplay muted (works on all browsers)
  useEffect(() => {
    if (!open || !videoRef.current) return;
    const v = videoRef.current;
    v.muted = true;
    const tryPlay = async () => {
      try {
        await v.play();
        setStarted(true);
      } catch {
        // Will require user click — fallback covered by the big play CTA
      }
    };
    tryPlay();
  }, [open]);

  const handleUnmute = async () => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    setMuted(false);
    try {
      await videoRef.current.play();
    } catch {
      /* noop */
    }
  };

  const handleFinish = () => {
    markCoachYumixIntroSeen();
    onClose?.();
  };

  const t = (fr, en) => (language === 'fr' ? fr : en);

  // Iter. 406.fr.22 — Source video is 1280×720 LANDSCAPE while phones are
  // portrait → `object-cover` was zooming massively to fill the height and
  // pushed the video off-screen ("plus grande que mon écran / je dois la
  // remonter"). We now (a) use `object-contain` so the full frame is always
  // visible (letterboxed top/bottom if needed) and (b) lock body scroll
  // while the cinematic is mounted so the page can't drift sideways.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden"
          style={{ width: '100vw', height: '100dvh' }}
          data-testid="coach-yumix-intro-modal"
          role="dialog"
          aria-modal="true"
          aria-label={t('Présentation de Coach YUMIX', 'Coach YUMIX Introduction')}
        >
          {/* Video player */}
          <video
            ref={videoRef}
            src="/assets/coach-yumix-intro.mp4"
            poster="/assets/coach-yumix-intro-poster.png"
            playsInline
            autoPlay
            muted={muted}
            onEnded={() => setEnded(true)}
            onPlay={() => setStarted(true)}
            className="object-contain bg-black"
            style={{ width: '100%', height: '100%', maxWidth: '100vw', maxHeight: '100dvh' }}
            data-testid="coach-yumix-intro-video"
          />

          {/* Top vignette + branding */}
          <div className="absolute inset-x-0 top-0 pointer-events-none h-32 bg-gradient-to-b from-black/85 via-black/35 to-transparent" />
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-auto">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-300" style={{ filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.8))' }} />
              <span className="text-[10px] uppercase tracking-[0.32em] font-bold text-orange-200">
                {t('Cinématique d\'arrivée', 'Arrival cinematic')}
              </span>
            </div>
            {showSkip && !ended && (
              <button
                onClick={handleFinish}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all active:scale-95"
                data-testid="coach-yumix-intro-skip-btn"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(15,15,15,0.6) 45%, rgba(251,146,60,0.14) 100%)',
                  border: '1px solid rgba(251,146,60,0.4)',
                  boxShadow: '0 0 10px rgba(251,146,60,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <SkipForward
                  className="w-3.5 h-3.5"
                  style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 4px rgba(251,146,60,0.8))' }}
                />
                <span
                  className="text-[11px] font-black uppercase tracking-widest"
                  style={{ color: '#fed7aa', textShadow: '0 0 5px rgba(251,146,60,0.55)' }}
                >
                  {t('Passer', 'Skip')}
                </span>
              </button>
            )}
          </div>

          {/* Big "enable sound" CTA — visible only while muted */}
          {started && muted && !ended && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleUnmute}
              className="absolute bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-2xl"
              style={{ boxShadow: '0 0 32px rgba(251,146,60,0.65), 0 0 64px rgba(244,63,94,0.4)' }}
              data-testid="coach-yumix-intro-unmute-btn"
            >
              <Volume2 className="w-5 h-5" />
              <span className="text-sm font-bold uppercase tracking-wider">
                {t('Activer le son', 'Enable sound')}
              </span>
            </motion.button>
          )}

          {/* Toggle mute icon (top-right corner when sound is active) */}
          {started && !muted && !ended && (
            <button
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.muted = true;
                  setMuted(true);
                }
              }}
              className="absolute bottom-24 right-5 w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95"
              data-testid="coach-yumix-intro-mute-btn"
              aria-label={t('Couper le son', 'Mute')}
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(15,15,15,0.6) 45%, rgba(251,146,60,0.14) 100%)',
                border: '1px solid rgba(251,146,60,0.4)',
                boxShadow: '0 0 12px rgba(251,146,60,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <VolumeX
                className="w-5 h-5"
                style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.85))' }}
              />
            </button>
          )}

          {/* Final CTA overlay */}
          {ended && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 flex flex-col items-center justify-end pb-12 px-6"
              style={{
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.0) 100%)',
              }}
            >
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-center font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white mb-1"
                style={{ textShadow: '0 0 24px rgba(251,146,60,0.6)' }}
              >
                {t('N\'ayez pas peur,', 'Do not fear,')}
              </motion.p>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45, duration: 0.6 }}
                className="text-center font-heading text-3xl sm:text-4xl uppercase tracking-tight bg-gradient-to-r from-orange-300 via-red-300 to-amber-300 bg-clip-text text-transparent mb-6"
              >
                {t('le changement est là.', 'change is here.')}
              </motion.p>
              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleFinish}
                className="w-full max-w-sm px-6 py-4 rounded-2xl bg-gradient-to-r from-orange-500 via-red-500 to-orange-600 text-white font-heading text-base uppercase tracking-wider"
                style={{ boxShadow: '0 0 36px rgba(251,146,60,0.65), 0 8px 32px rgba(244,63,94,0.4)' }}
                data-testid="coach-yumix-intro-start-btn"
              >
                {t('Commencer l\'aventure', 'Start the journey')}
              </motion.button>
              <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-white/50">
                {t('— Coach YUMIX', '— Coach YUMIX')}
              </p>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CoachYumixIntroModal;
